import {
  ClipboardList,
  Code2,
  FileText,
  ListChecks,
  type LucideIcon,
  Pencil,
  Sparkles,
} from "lucide-react"

import type { MaterialType } from "@/types"

// PRD-11 + PRD-29 — single per-type icon map, shared across the
// materials cards, the material detail header, and the student-detail
// material sections. Centralized here (re-exported from
// features/materials/components/statusVisuals for back-compat) so
// non-materials features can reuse it without a cross-feature import
// (.claude/rules/architecture.md).
//
// `Partial` on purpose: `material.material_type` is typed as the narrow
// `MaterialType` union, but that's a compile-time promise the backend
// doesn't always keep at runtime — legacy rows can carry a type value
// that predates the current enum (e.g. "cuestionario", dropped from
// `Material.MaterialType` per apps/backend/tests/factories.py:121-123;
// the backend's own `is_gradeable_material_type` already treats any
// unrecognized code as back-compat-gradeable rather than erroring). A
// plain `Record` let every consumer do an unchecked `TYPE_ICONS[type]`
// and crash the whole card tree (`Element type is invalid`) the moment
// one such row loaded. `Partial` forces every call site to fall back —
// use `DEFAULT_TYPE_ICON` — instead of assuming the lookup can't miss.
export const TYPE_ICONS: Partial<Record<MaterialType, LucideIcon>> = {
  examen: FileText,
  test: ListChecks,
  tarea: ClipboardList,
  ejercicios: Pencil,
  // PRD-49 — Práctica de programación (manifest v12).
  codigo: Code2,
}

/** Fallback for a `material_type` value not in `TYPE_ICONS` (unknown or legacy). */
export const DEFAULT_TYPE_ICON: LucideIcon = Sparkles

// The label for every type a row can carry — including `cuestionario`, the
// legacy code the icon map above already warns about. A bare "type.<code>"
// lookup rendered the literal key "type.cuestionario" on a teacher's screen
// for any such row (PRD-61 review).
const MATERIAL_TYPE_LABEL_KEYS = {
  examen: "materials:type.examen",
  test: "materials:type.test",
  tarea: "materials:type.tarea",
  ejercicios: "materials:type.ejercicios",
  codigo: "materials:type.codigo",
  cuestionario: "materials:type.cuestionario",
} as const

type MaterialTypeLabelKey =
  | (typeof MATERIAL_TYPE_LABEL_KEYS)[keyof typeof MATERIAL_TYPE_LABEL_KEYS]
  | "materials:type.unknown"

/**
 * A material type's human label, never a raw i18n key: a code this build
 * does not know (legacy or newer than the SPA) reads as "Material".
 * `type` is `string` on purpose — the wire does not keep the union's promise.
 */
export function materialTypeLabel(
  t: (key: MaterialTypeLabelKey) => string,
  type: MaterialType | string,
): string {
  const key = Object.hasOwn(MATERIAL_TYPE_LABEL_KEYS, type)
    ? MATERIAL_TYPE_LABEL_KEYS[type as keyof typeof MATERIAL_TYPE_LABEL_KEYS]
    : "materials:type.unknown"
  return t(key)
}

// PRD-29 — mirrors apps/backend/core/material_types.py::
// is_gradeable_material_type. Practice-only types are send-only: no
// grade record, hidden from every grading surface. `ejercicios` is the
// only practice type today; keep this set in sync with the backend
// helper (the backend is canonical — see .claude/rules/magic-values.md).
const PRACTICE_MATERIAL_TYPES: ReadonlySet<MaterialType> = new Set<MaterialType>(["ejercicios"])

/** True when a material type is practice-only (non-graded). */
export function isPracticeMaterialType(type: MaterialType): boolean {
  return PRACTICE_MATERIAL_TYPES.has(type)
}

// PRD-43 §FR-5.1 / §FR-5.2 — the types the photo-grading pipeline accepts.
// Mirrors apps/backend/core/tasks.py::_GRADEABLE_MATERIAL_TYPES (and the same
// gate on `POST /api/progress/uploads/grade`); the backend is canonical, per
// `.claude/rules/magic-values.md`.
//
// NARROWER than `isPracticeMaterialType`: `test` creates a grade record but
// the judge does not grade it (product decision, §FR-5.4), so "has a gradebook
// cell" and "can be graded from a photo" are two different questions and need
// two different predicates.
//
// This set is what every "Calificar con foto" entry point gates on. Seven call
// sites used to hard-code `=== "examen"` inline, which is why turning tareas on
// would otherwise have meant hiding the entry point on every surface that
// offers it.
// PRD-50 — `codigo` joins the set: the backend's derived
// GRADEABLE_MATERIAL_TYPES (manifest `gradeable` minus the `test` product
// exclusion) accepts codigo uploads on POST /uploads/grade. A professor
// photo of a paper code submission routes STRAIGHT to manual review
// (§9.2 — no VLM, no OCR for codigo), but the entry point must exist or
// paper submissions could only arrive through the student portal.
const PHOTO_GRADEABLE_MATERIAL_TYPES: ReadonlySet<MaterialType> = new Set<MaterialType>([
  "examen",
  "tarea",
  "codigo",
])

/**
 * True when a material can be graded from a photo of the student's sheet.
 *
 * Accepts null / undefined because `Task.material_type` is nullable on rows
 * with no parent material — those are never photo-gradeable.
 */
export function isPhotoGradeableMaterialType(type: MaterialType | null | undefined): boolean {
  return type != null && PHOTO_GRADEABLE_MATERIAL_TYPES.has(type)
}
